<template>
  <Modal
    :modelValue="show"
    title=""
    size="large"
    @update:modelValue="handleModalClose"
  >
    <!-- Live region for screen readers -->
    <span class="sr-only" aria-live="polite">
      {{ step === 'form' ? 'Paso 1: Formulario de email' : 'Paso 2: Confirmar envío' }}
    </span>

    <Transition name="step" mode="out-in">
      <!-- Step 1: Form -->
      <div v-if="step === 'form'" key="form">
        <!-- Header -->
        <div class="flex items-center gap-3 mb-6">
          <div class="flex-shrink-0 w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center">
            <EnvelopeIcon class="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h3 class="text-lg font-medium text-slate-900">Enviar email</h3>
            <p class="text-sm text-slate-600">{{ recipientName }}</p>
          </div>
        </div>

        <!-- Form -->
        <div class="space-y-4">
          <!-- Recipient (read-only) -->
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-1">Destinatario</label>
            <div class="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg">
              <EnvelopeIcon class="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span class="text-sm text-slate-700">{{ recipientEmail }}</span>
            </div>
          </div>

          <!-- Subject -->
          <div>
            <label for="email-subject" class="block text-sm font-medium text-slate-700 mb-1">
              Asunto <span class="text-red-500">*</span>
            </label>
            <input
              id="email-subject"
              ref="subjectRef"
              v-model="subject"
              type="text"
              maxlength="200"
              aria-required="true"
              aria-describedby="subject-counter"
              class="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500 focus:outline-none transition-all duration-200"
              placeholder="Escribe el asunto del email..."
            />
            <div class="flex justify-end mt-1">
              <span
                id="subject-counter"
                :class="['text-xs tabular-nums', getCounterClass(subject.length, 200)]"
              >
                {{ subject.length }}/200
              </span>
            </div>
          </div>

          <!-- Message -->
          <div>
            <label for="email-message" class="block text-sm font-medium text-slate-700 mb-1">
              Mensaje <span class="text-red-500">*</span>
            </label>
            <textarea
              id="email-message"
              v-model="message"
              rows="6"
              maxlength="5000"
              aria-required="true"
              aria-describedby="message-counter"
              class="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500 focus:outline-none transition-all duration-200 resize-y min-h-[120px] max-h-[300px]"
              placeholder="Escribe el contenido del email..."
            ></textarea>
            <div class="flex justify-end mt-1">
              <span
                id="message-counter"
                :class="['text-xs tabular-nums', getCounterClass(message.length, 5000)]"
              >
                {{ message.length }}/5000
              </span>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-slate-200">
          <Button variant="outline" @click="handleClose">
            Cancelar
          </Button>
          <Button
            variant="primary"
            :disabled="!isFormValid"
            @click="goToConfirm"
          >
            Vista previa
          </Button>
        </div>
      </div>

      <!-- Step 2: Confirm -->
      <div v-else key="confirm">
        <!-- Header -->
        <div class="flex items-center gap-3 mb-6">
          <div class="flex-shrink-0 w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center">
            <EnvelopeIcon class="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h3 class="text-lg font-medium text-slate-900">Enviar email</h3>
            <p class="text-sm text-slate-600">Confirmar envío</p>
          </div>
        </div>

        <!-- Preview card -->
        <div
          ref="previewRef"
          tabindex="-1"
          class="bg-white border border-slate-200 rounded-xl p-4 space-y-4"
        >
          <div>
            <span class="text-xs font-medium text-slate-400 uppercase tracking-wide">Para</span>
            <p class="text-sm text-slate-700 mt-1">{{ recipientEmail }}</p>
          </div>
          <div>
            <span class="text-xs font-medium text-slate-400 uppercase tracking-wide">Asunto</span>
            <p class="text-sm font-medium text-slate-900 mt-1">{{ subject }}</p>
          </div>
          <div>
            <span class="text-xs font-medium text-slate-400 uppercase tracking-wide">Mensaje</span>
            <p class="text-sm text-slate-700 mt-1 whitespace-pre-wrap max-h-[200px] overflow-y-auto">{{ message }}</p>
          </div>
        </div>

        <!-- Warning -->
        <div class="bg-amber-50 border border-amber-200/60 rounded-xl p-4 mt-4" role="alert">
          <div class="flex items-start gap-3">
            <ExclamationTriangleIcon class="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p class="text-sm text-amber-800">
              Este email se enviará directamente al destinatario. Revisa el contenido antes de confirmar.
            </p>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-slate-200">
          <Button variant="outline" :disabled="isSending" @click="goToForm">
            Editar
          </Button>
          <Button
            variant="primary"
            :disabled="isSending"
            @click="handleSend"
          >
            <span v-if="isSending" class="flex items-center gap-2">
              <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Enviando...
            </span>
            <span v-else>Enviar email</span>
          </Button>
        </div>
      </div>
    </Transition>
  </Modal>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { EnvelopeIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import Modal from '@/components/ui/Modal.vue'
import Button from '@/components/ui/Button.vue'
import { useToast } from '@/composables/useToast'
import { adminService } from '@/services/adminService'

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  },
  recipientName: {
    type: String,
    required: true
  },
  recipientEmail: {
    type: String,
    required: true
  },
  initialSubject: {
    type: String,
    default: ''
  },
  context: {
    type: String,
    default: 'general'
  },
  caseId: {
    type: String,
    default: null
  },
  userId: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['close', 'sent'])

const toast = useToast()

// State
const step = ref('form')
const subject = ref(props.initialSubject)
const message = ref('')
const isSending = ref(false)

// Refs
const subjectRef = ref(null)
const previewRef = ref(null)

// Validation
const isFormValid = computed(() => {
  return subject.value.trim().length >= 5 && message.value.trim().length >= 10
})

// Character counter classes
const getCounterClass = (current, max) => {
  const ratio = current / max
  if (ratio > 0.95) return 'text-red-600 font-semibold'
  if (ratio > 0.80) return 'text-amber-600 font-medium'
  return 'text-slate-400'
}

// Navigation
const goToConfirm = async () => {
  step.value = 'confirm'
  await nextTick()
  previewRef.value?.focus()
}

const goToForm = async () => {
  step.value = 'form'
  await nextTick()
  subjectRef.value?.focus()
}

// Send
const handleSend = async () => {
  isSending.value = true
  try {
    if (props.context === 'case') {
      await adminService.sendCaseEmail(props.caseId, {
        subject: subject.value.trim(),
        message: message.value.trim()
      })
    } else if (props.context === 'user') {
      await adminService.sendUserEmail(props.userId, {
        subject: subject.value.trim(),
        message: message.value.trim()
      })
    }
    toast.success('Email enviado correctamente')
    emit('sent')
    emit('close')
  } catch (error) {
    toast.error(error.message || 'Error al enviar el email')
    step.value = 'form'
  } finally {
    isSending.value = false
  }
}

// Close handling
const handleClose = () => {
  if (!isSending.value) {
    emit('close')
  }
}

const handleModalClose = (val) => {
  if (!val && !isSending.value) {
    emit('close')
  }
}

// Reset on show change
watch(() => props.show, (newVal) => {
  if (newVal) {
    step.value = 'form'
    subject.value = props.initialSubject
    message.value = ''
    isSending.value = false
    nextTick(() => subjectRef.value?.focus())
  }
})
</script>

<style scoped>
@reference "../../assets/styles/main.css";

.step-enter-active, .step-leave-active {
  @apply transition-all duration-200 ease-in-out;
}

@media (prefers-reduced-motion: no-preference) {
  .step-enter-from { @apply opacity-0 translate-x-2; }
  .step-leave-to { @apply opacity-0 -translate-x-2; }
}

@media (prefers-reduced-motion: reduce) {
  .step-enter-from { @apply opacity-0; }
  .step-leave-to { @apply opacity-0; }
}
</style>
